// =====================================================================
// ERP platform deep-page data. One entry per /erp/{slug}.
// Drives the ErpPage template (hero, capabilities, stack, FAQ).
// =====================================================================

export const ERP_PAGES = {
  fusion: {
    slug: "fusion",
    countryCode: "Oracle Fusion",
    hero: {
      eyebrow: "Oracle Fusion Cloud",
      h1: "Oracle Fusion Cloud — implementation, BIP, OIC & VBCS",
      subhead:
        "End-to-end Oracle Fusion delivery: Procure-to-Pay, Record-to-Report and Order-to-Cash, plus BI Publisher, Oracle Integration Cloud and VBCS extensions.",
    },
    snapshot: [
      { label: "Modules", value: "P2P, R2R, O2C, SCM" },
      { label: "Reporting", value: "BIP / OTBI" },
      { label: "Integration", value: "OIC / REST / SOAP" },
      { label: "Extensions", value: "VBCS / APEX" },
    ],
    whyTitle: "What we deliver on Fusion",
    whyLede:
      "From greenfield implementation to deep technical extension — and the e-invoicing layer most partners leave out.",
    features: [
      { icon: "Database", title: "Implementation", body: "Discovery to go-live across Financials, Procurement, SCM and Projects, with clean configuration and conference-room pilots." },
      { icon: "ChartLine", title: "BI Publisher & OTBI", body: "Custom BIP templates, bursting, OTBI dashboards and report performance optimization for period close." },
      { icon: "PlugsConnected", title: "Oracle Integration Cloud", body: "OIC orchestrations and integrations across Fusion, third-party apps and e-invoicing ASPs — REST, SOAP and file-based." },
      { icon: "CodeBlock", title: "VBCS & APEX extensions", body: "Visual Builder UI extensions and APEX tools that sit cleanly alongside Fusion without breaking upgrade paths." },
      { icon: "Receipt", title: "E-invoicing built in", body: "ZATCA, GST, LHDN and PINT AE wired into Fusion via OIC or middleware — ASP-neutral, audit-ready." },
      { icon: "ListChecks", title: "Managed services", body: "Post go-live AMS: enhancements, quarterly update regression, and support aligned to your close calendar." },
    ],
    stack: ["Oracle Fusion Financials", "Procurement", "SCM", "OIC", "BI Publisher", "OTBI", "VBCS", "APEX", "FBDI / ADFdi"],
    faqs: [
      { question: "Do you do greenfield Fusion implementations or only extensions?", answer: "Both. We run full implementations from discovery to go-live and knowledge transfer, and we also take on focused technical work — BIP, OIC, VBCS — on existing Fusion environments." },
      { question: "When should I use OIC versus VBCS versus APEX?", answer: "Use OIC for system-to-system integration and orchestration, VBCS for Oracle-native UI extensions to Fusion, and APEX for data-centric internal tools over the database. We often combine them — OIC for the e-invoicing pipeline and VBCS/APEX for the finance team's operational dashboards." },
      { question: "Can you make Fusion ZATCA / GST / PINT AE compliant?", answer: "Yes. We connect Fusion to the relevant authority via OIC or a middleware layer, generate the compliant format, and handle clearance or reporting — ASP-neutral, without breaking your upgrade path." },
    ],
  },

  ebs: {
    slug: "ebs",
    countryCode: "Oracle EBS",
    hero: {
      eyebrow: "Oracle E-Business Suite",
      h1: "Oracle EBS — implementation, PL/SQL, upgrades & support",
      subhead:
        "Oracle E-Business Suite (R12 / R12.2) implementation, customization, PL/SQL and Forms development, upgrades and managed support — plus e-invoicing integration.",
    },
    snapshot: [
      { label: "Versions", value: "R12 / R12.2" },
      { label: "Tech", value: "PL/SQL, Forms, OAF" },
      { label: "Reporting", value: "XML/BI Publisher" },
      { label: "Integration", value: "REST / SOAP / OIC" },
    ],
    whyTitle: "What we deliver on EBS",
    whyLede:
      "Deep EBS technical capability for organisations that aren't moving to Fusion before their next compliance deadline.",
    features: [
      { icon: "Database", title: "Implementation & upgrades", body: "EBS implementation, R12.2 upgrades and re-implementations across Financials, SCM and HRMS." },
      { icon: "CodeBlock", title: "PL/SQL & Forms", body: "Custom PL/SQL packages, Oracle Forms, OAF personalizations and interfaces built to EBS standards." },
      { icon: "ChartLine", title: "XML / BI Publisher", body: "Custom invoice, cheque and statutory report templates with bursting and localization." },
      { icon: "Receipt", title: "E-invoicing integration", body: "GST, ZATCA and PINT AE connected to EBS through an integration layer — no EBS re-implementation required." },
      { icon: "PlugsConnected", title: "Interfaces & integrations", body: "Inbound/outbound interfaces, REST/SOAP services and OIC connectivity to modern apps and ASPs." },
      { icon: "ListChecks", title: "Managed support", body: "Production support, period-close support and enhancement delivery for EBS estates." },
    ],
    stack: ["EBS R12.2", "PL/SQL", "Oracle Forms", "OA Framework", "XML Publisher", "Workflow", "AOL", "OIC"],
    faqs: [
      { question: "Can you make older EBS releases e-invoicing compliant?", answer: "Yes. This is a common pattern — we connect EBS to the tax authority through an integration layer that reads invoice data, generates the compliant XML, applies stamps/QR and handles clearance or reporting, without re-implementing EBS." },
      { question: "Do you support R12.2 upgrades?", answer: "Yes — R12.2 upgrades, re-implementations and the associated custom-code remediation (PL/SQL, Forms, OAF) are core EBS work for us." },
      { question: "Should I stay on EBS or move to Fusion?", answer: "It depends on your roadmap and deadlines. We often keep EBS compliant now via integration while planning a Fusion move separately, so a mandate date doesn't force a rushed migration." },
    ],
  },

  sap: {
    slug: "sap",
    countryCode: "SAP",
    hero: {
      eyebrow: "SAP S/4HANA & ECC",
      h1: "SAP e-invoicing & integration — S/4HANA and ECC",
      subhead:
        "Connect SAP S/4HANA or ECC to global e-invoicing mandates and downstream systems, with clean, upgrade-safe integration to your chosen ASP or PEPPOL access point.",
    },
    snapshot: [
      { label: "Systems", value: "S/4HANA, ECC" },
      { label: "Integration", value: "IDoc, BAPI, OData, CPI" },
      { label: "Compliance", value: "ZATCA, GST, PINT AE" },
      { label: "Cloudare role", value: "Integration partner" },
    ],
    whyTitle: "What we deliver on SAP",
    whyLede:
      "We are not your core SAP implementer — we are the team that makes SAP talk to compliance and the rest of your estate.",
    features: [
      { icon: "PlugsConnected", title: "E-invoicing integration", body: "SAP-to-ASP / PEPPOL integration for ZATCA, GST, LHDN and PINT AE via IDoc, OData or middleware." },
      { icon: "ArrowsLeftRight", title: "Interfaces", body: "IDoc, BAPI, RFC and OData interfaces between SAP and third-party apps, banks and tax platforms." },
      { icon: "Receipt", title: "Format generation", body: "Generate UBL 2.1, PINT AE and GST formats from SAP billing data with pre-submission validation." },
      { icon: "ChartLine", title: "Reconciliation", body: "Match authority responses back into SAP, with exception handling and audit trails." },
      { icon: "ShieldCheck", title: "Validation layer", body: "Stop rejections before they happen with schema and business-rule validation ahead of submission." },
      { icon: "ListChecks", title: "Support", body: "Ongoing support of the integration layer across SAP transports and mandate changes." },
    ],
    stack: ["S/4HANA", "SAP ECC", "IDoc", "BAPI / RFC", "OData", "SAP CPI", "PI/PO"],
    faqs: [
      { question: "Do you implement SAP from scratch?", answer: "Our focus is integration and e-invoicing rather than core SAP implementation. We work alongside your SAP team or implementation partner to connect SAP to compliance and surrounding systems." },
      { question: "Which SAP integration methods do you use?", answer: "IDoc, BAPI/RFC, OData and SAP CPI/PI-PO, depending on your landscape and whether you are on S/4HANA or ECC. We pick the upgrade-safe option for your estate." },
      { question: "Can you connect SAP to a PEPPOL access point?", answer: "Yes — we connect SAP to PEPPOL access points and ASPs for EU, UAE and other mandates, keeping the integration access-point-neutral." },
    ],
  },

  dynamics: {
    slug: "dynamics",
    countryCode: "MS Dynamics",
    hero: {
      eyebrow: "Microsoft Dynamics 365",
      h1: "Microsoft Dynamics 365 — e-invoicing & integration",
      subhead:
        "Connect Dynamics 365 Finance & Operations or Business Central to global e-invoicing mandates and your wider application landscape.",
    },
    snapshot: [
      { label: "Products", value: "D365 F&O, BC" },
      { label: "Integration", value: "Power Platform, APIs" },
      { label: "Compliance", value: "GST, ZATCA, PINT AE" },
      { label: "Cloudare role", value: "Integration partner" },
    ],
    whyTitle: "What we deliver on Dynamics",
    whyLede:
      "E-invoicing and integration for Dynamics 365, using the Microsoft stack you already run.",
    features: [
      { icon: "PlugsConnected", title: "E-invoicing integration", body: "Dynamics-to-ASP / PEPPOL connectivity for GST, ZATCA, LHDN and PINT AE." },
      { icon: "CodeBlock", title: "Power Platform", body: "Power Automate flows and connectors that bridge Dynamics to tax platforms and approvals." },
      { icon: "Receipt", title: "Format generation", body: "Generate compliant invoice formats from D365 transactions with validation before submission." },
      { icon: "ArrowsLeftRight", title: "Data integration", body: "Dual-write, Dataverse and API-based integration to surrounding apps." },
      { icon: "ChartLine", title: "Reporting", body: "Operational reporting on submission status, rejections and reconciliation." },
      { icon: "ListChecks", title: "Support", body: "Ongoing support across Dynamics updates and mandate changes." },
    ],
    stack: ["D365 Finance & Operations", "Business Central", "Power Automate", "Dataverse", "Azure Functions"],
    faqs: [
      { question: "Which Dynamics products do you support?", answer: "Dynamics 365 Finance & Operations and Business Central, using the Power Platform and API-based integration to connect them to e-invoicing and surrounding systems." },
      { question: "Do you use the Microsoft Electronic Invoicing service?", answer: "We work with the native Dynamics electronic invoicing capabilities where they fit, and add ASP/PEPPOL connectivity and validation where the mandate needs more than the box provides." },
    ],
  },

  odoo: {
    slug: "odoo",
    countryCode: "Odoo",
    hero: {
      eyebrow: "Odoo ERP",
      h1: "Odoo ERP — implementation, customization & e-invoicing",
      subhead:
        "Odoo implementation, module customization and e-invoicing compliance for mid-market businesses — Community or Enterprise.",
    },
    snapshot: [
      { label: "Editions", value: "Community / Enterprise" },
      { label: "Tech", value: "Python, XML, QWeb" },
      { label: "Compliance", value: "GST, ZATCA, PINT AE" },
      { label: "Cloudare role", value: "Implementation partner" },
    ],
    whyTitle: "What we deliver on Odoo",
    whyLede:
      "Right-sized Odoo delivery — implemented, customized and made compliant — for businesses that want capability without enterprise overhead.",
    features: [
      { icon: "Database", title: "Implementation", body: "Odoo implementation across Accounting, Sales, Inventory, Purchase and Manufacturing." },
      { icon: "CodeBlock", title: "Custom modules", body: "Python/XML module development, QWeb reports and workflow customization to your processes." },
      { icon: "Receipt", title: "E-invoicing", body: "GST IRN, ZATCA and PINT AE compliance built into Odoo invoicing workflows." },
      { icon: "ArrowsLeftRight", title: "Migration", body: "Migration from Tally, QuickBooks and legacy systems into Odoo with clean data mapping." },
      { icon: "PlugsConnected", title: "Integrations", body: "Connect Odoo to payment, logistics, banking and tax platforms via APIs." },
      { icon: "ListChecks", title: "Support & AMC", body: "Version upgrades, enhancements and support under a predictable AMC." },
    ],
    stack: ["Odoo 17 / Enterprise", "Python", "XML / QWeb", "PostgreSQL", "XML-RPC / REST"],
    faqs: [
      { question: "Community or Enterprise — which Odoo edition?", answer: "It depends on the apps and support you need. We help you choose based on functional fit and total cost, and we implement either edition." },
      { question: "Can Odoo handle GST e-invoicing in India?", answer: "Yes — we build IRN generation, e-way bill and the signed QR into Odoo invoicing, integrated with an authorised IRP/GSP." },
      { question: "Can you migrate us from Tally or QuickBooks?", answer: "Yes. Migration from Tally, QuickBooks and other legacy systems into Odoo, including chart-of-accounts and historical data mapping, is a common engagement." },
    ],
  },
};

export const ERP_ORDER = ["fusion", "ebs", "sap", "dynamics", "odoo"];
